/* Motion Molds: cart drawer */
/* Split out of global.js on 2026-10-19 so each theme file stays under the
   Admin API upload limit. Loaded before global.js in layout/theme.liquid and
   exposed as window.MMCart, so the offer cards (mm-bundle), the homepage Set
   button and the "complete the Set" nudge all share this one drawer render. */

(function () {
  'use strict';

  const fmtMoney = (cents) => {
    const n = (cents / 100).toFixed(2);
    return '$' + n;
  };

  // ──────────────────────────────────────────────
  // CART
  // ──────────────────────────────────────────────
  const Cart = {
    state: null,

    async fetch() {
      const res = await fetch(window.MM.routes.cart, { credentials: 'same-origin' });
      this.state = await res.json();
      await this.syncGift();
      return this.state;
    },

    // The free gift box rides along with The Complete Set as one extra $0
    // line (product free-gift-box, window.MM.gift) whenever all three molds
    // are in the cart, so the drawer, the cart page, checkout, the order
    // email and the packing slip all list it. Only this code adds or removes
    // it: the line has no quantity box and no Remove. Quantity follows the
    // smallest mold quantity, so two Sets get two gift boxes and a cart that
    // loses a mold loses the gift.
    giftHandle() { return (window.MM && window.MM.gift && window.MM.gift.handle) || ''; },
    async syncGift() {
      const gift = window.MM && window.MM.gift;
      const molds = (window.MM && window.MM.molds) || [];
      if (!gift || !gift.variant || !this.state || this._giftBusy || molds.length !== 3) return this.state;
      const items = this.state.items || [];
      const qtys = molds.map(m => items.filter(it => it.handle === m.handle).reduce((n, it) => n + it.quantity, 0));
      const want = Math.min.apply(null, qtys);
      const line = items.find(it => it.handle === gift.handle);
      const have = line ? line.quantity : 0;
      if (want === have) return this.state;
      this._giftBusy = true;
      try {
        if (!line) {
          const res = await fetch(window.MM.routes.cart_add + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(gift.variant, 10), quantity: want }] })
          });
          if (!res.ok) throw new Error('Gift add failed');
          const r2 = await fetch(window.MM.routes.cart, { credentials: 'same-origin' });
          this.state = await r2.json();
        } else {
          const res = await fetch(window.MM.routes.cart_change + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: want })
          });
          if (!res.ok) throw new Error('Gift change failed');
          this.state = await res.json();
        }
      } catch (e) {
        // Leave the cart as it is: the Set still checks out, only the gift line is missing.
      } finally {
        this._giftBusy = false;
      }
      return this.state;
    },

    // A pre-order mold (sold out, set to keep selling) gets a line property so
    // the cart, checkout, order and confirmation email all say when it ships.
    preorderProps(variantId) {
      const molds = (window.MM && window.MM.molds) || [];
      const win = (window.MM && window.MM.preorder_window) || '';
      const m = molds.find(x => String(x.variant) === String(variantId));
      return (m && m.preorder && win) ? { 'Pre-order': 'Ships ' + win } : null;
    },
    async add(variantId, quantity) {
      quantity = quantity || 1;
      const item = { id: parseInt(variantId, 10), quantity: quantity };
      const props = this.preorderProps(variantId);
      if (props) item.properties = props;
      const res = await fetch(window.MM.routes.cart_add + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await this.fetch();
      this.render();
      this.open();
      return this.state;
    },

    // Several variants in one request: the Set on the homepage, the
    // "complete the Set" nudge in the drawer.
    async addMany(variantIds, quantity) {
      quantity = quantity || 1;
      const items = variantIds.map(id => {
        const it = { id: parseInt(id, 10), quantity: quantity };
        const props = this.preorderProps(id);
        if (props) it.properties = props;
        return it;
      });
      const res = await fetch(window.MM.routes.cart_add + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await this.fetch();
      this.render();
      this.open();
      return this.state;
    },

    async change(lineKey, quantity) {
      const res = await fetch(window.MM.routes.cart_change + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
      if (!res.ok) throw new Error('Cart update failed');
      this.state = await res.json();
      await this.syncGift();
      this.render();
      return this.state;
    },

    render() {
      const body = document.getElementById('cart-drawer-body');
      const foot = document.getElementById('cart-drawer-foot');
      const subEl = document.getElementById('cart-drawer-subtotal-amt');
      if (!body) return;

      // update count
      const count = this.state ? this.state.item_count : 0;
      document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = count; });

      if (!this.state || !this.state.items.length) {
        body.innerHTML =
          '<div class="cart-drawer-empty">' +
            '<h4>Your Cart Is Empty.</h4>' +
            '<p>Pick a mold and start training the bottom half.</p>' +
            '<a href="/collections/all-molds">Shop The Molds →</a>' +
          '</div>';
        if (foot) foot.hidden = true;
        return;
      }
      if (foot) foot.hidden = false;

      // The Complete Set is three separate lines in the cart. The automatic
      // "The Complete Set" discount (buy 2 molds, get 1 at $29.98 off, once
      // per three molds) takes the Set saving off those lines, and the
      // shipping rates ship any cart over $100 free, so the drawer
      // only has to SHOW it: a struck original price on discounted lines, a
      // discount row above the total and a shipping note. With one or two
      // molds in the cart it offers the missing ones at the price difference.
      // Data comes from window.MM.molds and window.MM.set_price (cents), set
      // in layout/theme.liquid.
      const molds = (window.MM && window.MM.molds) || [];
      const setPrice = (window.MM && window.MM.set_price) || 0;
      const setLive = !!(window.MM && window.MM.set_live);
      const preWin = (window.MM && window.MM.preorder_window) || '';
      const preHandles = molds.filter(m => m.preorder).map(m => m.handle);
      const have = molds.filter(m => this.state.items.some(it => it.handle === m.handle));
      const hasSet = molds.length === 3 && have.length === 3;
      const moldQty = this.state.items.reduce((n, it) => n + (molds.some(m => m.handle === it.handle) ? it.quantity : 0), 0);
      const giftH = this.giftHandle();
      const shippingBar = hasSet
        ? '<div class="cart-shipping-bar"><p><span class="check">✓</span>The Complete Set: free US shipping and a free gift box.</p></div>'
        : moldQty >= 2
          ? '<div class="cart-shipping-bar"><p><span class="check">✓</span>Free US shipping unlocked.</p></div>'
          : moldQty === 1
            ? '<div class="cart-shipping-bar"><p>Add a second mold and shipping is free.</p></div>'
            : '';

      // The gift box line sits last, under the molds it comes with.
      const lines = this.state.items.slice().sort((a, b) => (a.handle === giftH ? 1 : 0) - (b.handle === giftH ? 1 : 0));
      body.innerHTML = shippingBar + lines.map(item => {
        if (giftH && item.handle === giftH) {
          return '<div class="cart-item cart-item-gift">' +
            '<img src="' + (item.image || '') + '" alt="' + item.product_title + '">' +
            '<div class="cart-item-info">' +
              '<div class="cart-item-name">' + item.product_title + (item.quantity > 1 ? ' × ' + item.quantity : '') + '</div>' +
              '<div class="cart-item-price"><span class="cart-item-free">Free</span><div class="cart-item-pre">Included with The Complete Set</div></div>' +
            '</div>' +
          '</div>';
        }
        const struck = item.original_line_price > item.final_line_price
          ? '<s class="cart-item-was">' + fmtMoney(item.original_line_price) + '</s> ' : '';
        const preNote = (item.properties && item.properties['Pre-order']) ||
          ((preHandles.indexOf(item.handle) !== -1 && preWin) ? 'Ships ' + preWin : '');
        const pre = preNote ? '<div class="cart-item-pre">Pre-order · ' + preNote + '</div>' : '';
        return '<div class="cart-item">' +
          '<img src="' + (item.image || '') + '" alt="' + item.product_title + '">' +
          '<div class="cart-item-info">' +
            '<div class="cart-item-name">' + item.product_title + '</div>' +
            '<div class="cart-item-price">' + struck + fmtMoney(item.final_line_price) + pre + '</div>' +
            '<div class="cart-item-qty">' +
              '<button data-line="' + item.key + '" data-qty="' + Math.max(0, item.quantity - 1) + '" aria-label="Decrease">−</button>' +
              '<span>' + item.quantity + '</span>' +
              '<button data-line="' + item.key + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase">+</button>' +
            '</div>' +
          '</div>' +
          '<button class="cart-item-remove" data-line="' + item.key + '" data-qty="0" aria-label="Remove">×</button>' +
        '</div>';
      }).join('');

      // "Complete the Set": one card per missing mold, each added on its own
      // (2026-11-04: he wants shoppers to build up to the Set a mold at a
      // time, not jump straight to it). Once all three are in the cart the
      // automatic "The Complete Set" discount (buy 2 molds, get 1 at $29.98
      // off, repeating for every three) and the free gift box line apply on
      // their own. Shown only while every missing mold can actually be bought.
      const xsell = document.getElementById('cart-xsell-dyn');
      if (xsell) {
        const missing = molds.filter(m => !have.some(h => h.handle === m.handle));
        const offer = setLive && have.length > 0 && missing.length > 0 && missing.length < 3 &&
          setPrice > 0 && missing.every(m => m.available && m.variant);
        if (offer) {
          const separately = molds.reduce((sum, m) => sum + m.price, 0);
          const save = separately - setPrice;
          const lead = (missing.length === 2 ? 'Add both and all three come to ' : 'Add it and all three come to ') +
            fmtMoney(setPrice) + (save > 0 ? ', saving ' + fmtMoney(save) : '') + ', with free shipping and a free gift box.';
          xsell.innerHTML =
            '<div class="cart-xsell">' +
              '<div class="cart-xsell-head"><span class="cart-xsell-title">Complete The Set</span></div>' +
              '<p class="cart-xsell-lead">' + lead + '</p>' +
              '<div class="cart-xsell-list">' +
                missing.map(m =>
                  '<div class="cart-xsell-card cart-xsell-set">' +
                    '<div class="cart-xsell-img"><img src="' + (m.image || '') + '" alt="' + m.title + '" width="52" height="52" loading="lazy"></div>' +
                    '<div class="cart-xsell-meta">' +
                      '<div class="cart-xsell-name">+ ' + m.title + (m.preorder ? ' (pre-order)' : '') + '</div>' +
                      '<div class="cart-xsell-price">' + fmtMoney(m.price) + '</div>' +
                    '</div>' +
                    '<button type="button" class="cart-xsell-add" data-variants="' + m.variant + '">+ Add</button>' +
                  '</div>'
                ).join('') +
              '</div>' +
            '</div>';
          xsell.hidden = false;
        } else {
          xsell.innerHTML = '';
          xsell.hidden = true;
        }
      }

      // Discount row and total. total_discount already holds the Set saving
      // (and any code the shopper applied); the label is whatever Shopify
      // calls the discount, so it reads "The Complete Set" for the Set.
      const titles = [];
      this.state.items.forEach(it => (it.line_level_discount_allocations || []).forEach(a => {
        const t = a.discount_application && a.discount_application.title;
        if (t && titles.indexOf(t) === -1) titles.push(t);
      }));
      (this.state.cart_level_discount_applications || []).forEach(a => {
        if (a.title && titles.indexOf(a.title) === -1) titles.push(a.title);
      });
      const disc = this.state.total_discount || 0;
      const discRow = document.getElementById('cart-drawer-discount');
      const discLabel = document.getElementById('cart-drawer-discount-label');
      const discAmt = document.getElementById('cart-drawer-discount-amt');
      if (discRow) discRow.hidden = !(disc > 0);
      if (discLabel) discLabel.textContent = titles.length ? titles.join(' + ') : 'Discount';
      if (discAmt) discAmt.textContent = '−' + fmtMoney(disc);
      if (subEl) subEl.textContent = fmtMoney(this.state.total_price);
    },

    open() {
      const overlay = document.getElementById('cart-drawer-overlay');
      const drawer = document.getElementById('cart-drawer');
      if (overlay) overlay.classList.add('visible');
      if (drawer) drawer.classList.add('visible');
      document.documentElement.style.overflow = 'hidden';
    },
    close() {
      const overlay = document.getElementById('cart-drawer-overlay');
      const drawer = document.getElementById('cart-drawer');
      if (overlay) overlay.classList.remove('visible');
      if (drawer) drawer.classList.remove('visible');
      document.documentElement.style.overflow = '';
    },

    bind() {
      // Cart toggle in nav
      document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      });

      // Close handlers
      const closeBtn = document.getElementById('cart-drawer-close');
      const overlay = document.getElementById('cart-drawer-overlay');
      const drawer = document.getElementById('cart-drawer');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());
      if (overlay) overlay.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && drawer && drawer.classList.contains('visible')) this.close();
      });

      // Body delegation: qty + remove
      const body = document.getElementById('cart-drawer-body');
      if (body) {
        body.addEventListener('click', (e) => {
          const line = e.target.dataset && e.target.dataset.line;
          if (!line) return;
          const qty = parseInt(e.target.dataset.qty, 10);
          this.change(line, qty);
        });
      }

      // Checkout
      const checkout = document.querySelector('.cart-drawer-checkout');
      if (checkout) {
        checkout.addEventListener('click', () => { window.location = window.MM.routes.cart_url + '/checkout'; });
      }

      // Add-to-cart forms (Shopify forms)
      document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          const id = form.querySelector('[name="id"]').value;
          const qtyInput = form.querySelector('[name="quantity"]');
          const quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
          try { await this.add(id, quantity); }
          catch (err) { console.error(err); alert('Could not add to cart.'); }
        });
      });

      // Quick-add buttons with data-variant-id
      document.querySelectorAll('[data-variant-id]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.add(btn.dataset.variantId, 1);
        });
      });

      // Multi-add buttons with data-variants="id,id,id": the Set button on the
      // homepage and the "complete the Set" nudge, which is rendered later.
      // The offer cards inside #bundle-box carry the same attribute but run
      // their own add, so they are left alone here.
      document.addEventListener('click', (e) => {
        const btn = e.target && e.target.closest ? e.target.closest('[data-variants]') : null;
        if (!btn || btn.closest('#bundle-box')) return;
        e.preventDefault();
        const ids = (btn.getAttribute('data-variants') || '').split(',').map(x => x.trim()).filter(Boolean);
        if (!ids.length) return;
        btn.disabled = true;
        this.addMany(ids, 1)
          .catch(() => { alert('Could not add to cart.'); })
          .then(() => { btn.disabled = false; });
      });
    }
  };
  window.MMCart = Cart;
})();